import styles from "./Us30Page.module.css";
import videoPlaceholderImage from "../../assets/images/video-player-placeholder.png";
import api from "../../services/api";
import { ENDPOINTS } from "../../utils/endpoints";
import { useState, useEffect } from "react";
import useDashboardStore from "../../store/dashboardStore";
import { FaSpinner } from "react-icons/fa";
import useAuthStore from "../../store/authStore";
import { toast } from "react-toastify";
import AttachmentLink from "../Community/AttachmentLink";

// ─── ACCESS CHECK ─────────────────────────────────────────────────────────────
// Mirrors Flutter PageContentScreen._checkVideoAccess
// membershipExpiry: [{membership_id, expires_at}]
// userMembershipIds: number[]
// isAdmin: bool
// hasActiveIap: bool
const checkVideoAccess = (
  membershipExpiry,
  userMembershipIds,
  isAdmin,
  hasActiveIap,
) => {
  if (isAdmin) return true;
  if (!membershipExpiry || membershipExpiry.length === 0) return true; // no expiry = free

  const now = new Date();

  for (const entry of membershipExpiry) {
    const { membership_id, expires_at } = entry;

    // membership_id 0 = free tier
    if (membership_id === 0) {
      if (expires_at === null) return true; // lifetime free
      if (new Date(expires_at) > now) return true; // free and not expired
      continue; // free but expired — keep checking paid
    }

    // Paid membership entry
    if (hasActiveIap) return true; // IAP covers all paid plans

    if (userMembershipIds.includes(Number(membership_id))) {
      if (expires_at === null) return true; // lifetime paid
      if (new Date(expires_at) > now) return true; // paid and not expired
    }
  }

  return false;
};

const Us30Page = () => {
  const { setIsZoomMeetingScreen, setSelectedZoomSession } =
    useDashboardStore();
  const [us30, setUs30] = useState(null);
  const [us30Content, setUs30Content] = useState(null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPopup, setShowPopup] = useState(false);
  const [thumbnailCache, setThumbnailCache] = useState({});
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const { user, userMeta, refreshIapStatus } = useAuthStore();
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [showPlanSelection, setShowPlanSelection] = useState(false);
  const [availablePlans, setAvailablePlans] = useState([]);

  const role = user?.roles?.[0];
  let userMembershipIds = [];
  if (Array.isArray(userMeta?.membership_ids)) {
    userMembershipIds = userMeta.membership_ids.map(Number);
  } else if (typeof userMeta?.membership_ids === "string") {
    try {
      userMembershipIds = JSON.parse(userMeta.membership_ids).map(Number);
    } catch {
      userMembershipIds = [];
    }
  }

  useEffect(() => {
    const fetchUS30Data = async () => {
      try {
        const { data } = await api.get(ENDPOINTS.GET_US30_LIVE_SESSION);
        if (data.success) setUs30(data.session);
      } catch (err) {
        console.error("Failed to fetch us30 data", err);
      }
    };
    fetchUS30Data();
  }, []);

  useEffect(() => {
    const fetchUS30Content = async () => {
      try {
        const { data } = await api.get(ENDPOINTS.GET_US30_CONTENT);
        if (data.success) setUs30Content(data.content);
      } catch (err) {
        console.error("Failed to fetch us30 content", err);
      } finally {
        setLoading(false);
      }
    };
    fetchUS30Content();
  }, []);

  useEffect(() => {
    refreshIapStatus();
  }, []);

  useEffect(() => {
    // Prefer new key, fallback to old
    const videoTabs = us30Content?.video_tabs || us30Content?.video_links || [];
    if (videoTabs.length === 0) return;

    const load = async () => {
      const cache = {};
      for (const tab of videoTabs) {
        for (const group of tab.groups || []) {
          for (const video of group.videos || []) {
            if (!video.cover_image) {
              const key = `${tab.id}-${group.id}-${video.id}`;
              cache[key] =
                (await getVideoThumbnail(video.url)) || videoPlaceholderImage;
            }
          }
        }
      }
      setThumbnailCache(cache);
    };

    load();
  }, [us30Content]);

  useEffect(() => {
    if (!us30?.start_datetime) return;
    const calculateTimeLeft = () => {
      const difference = new Date(us30.start_datetime) - new Date();
      if (difference <= 0) return null;
      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / (1000 * 60)) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    };
    const timer = setInterval(() => {
      const updated = calculateTimeLeft();
      if (!updated) clearInterval(timer);
      setTimeLeft(updated);
    }, 1000);
    return () => clearInterval(timer);
  }, [us30]);

  function getYoutubeThumbnail(url) {
    const regex = /(?:youtube\.com\/.*v=|youtu\.be\/)([^&?/]+)/;
    const match = url.match(regex);
    return match
      ? `https://img.youtube.com/vi/${match[1]}/hqdefault.jpg`
      : null;
  }

  // Determine which image to show based on platform
  const isMobileApp = () => {
    return /flutter|webview/i.test(navigator.userAgent);
  };

  async function getVimeoThumbnail(url) {
    const regex = /vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/;
    const match = url.match(regex);
    if (!match || !match[1]) return null;
    try {
      const response = await fetch(
        `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(url)}`,
      );
      if (response.ok) {
        const data = await response.json();
        if (data.thumbnail_url) return data.thumbnail_url;
      }
    } catch (error) {
      console.error("Vimeo API error:", error);
    }
    return `https://vumbnail.com/${match[1]}.jpg`;
  }

  async function getVideoThumbnail(url) {
    if (url.includes("vimeo.com")) return await getVimeoThumbnail(url);
    if (url.includes("youtube.com") || url.includes("youtu.be"))
      return getYoutubeThumbnail(url);
    return videoPlaceholderImage;
  }

  const handleMembershipClick = async (membershipExpiry) => {
    if (isPurchasing || isLoadingPlans) return;
    setIsLoadingPlans(true);

    try {
      // Extract paid membership IDs from expiry array (exclude id=0 which is free)
      const paidIds = (membershipExpiry || [])
        .filter((e) => e.membership_id !== 0)
        .map((e) => e.membership_id);

      if (!paidIds.length) {
        toast.error("No membership plans configured for this video");
        return;
      }

      if (paidIds.length === 1) {
        handlePurchaseMembership(paidIds[0]);
      } else {
        const { data } = await api.get(ENDPOINTS.GET_ALL_MEMBERSHIPS, {
          params: { hide_restricted: true },
        });
        const allPlans = data.memberships || [];
        setAvailablePlans(allPlans.filter((plan) => paidIds.includes(plan.id)));
        setShowPlanSelection(true);
      }
    } catch (error) {
      console.error("Error loading membership plans:", error);
      toast.error("Failed to load membership plans");
    } finally {
      setIsLoadingPlans(false);
    }
  };

  const handlePurchaseMembership = async (membershipPlanId) => {
    if (!membershipPlanId || isPurchasing) return;
    setIsPurchasing(true);
    try {
      const response = await api.post(ENDPOINTS.PURCHASE_LINK, {
        user_id: user.id,
        lid: membershipPlanId,
      });
      if (response.data.success) {
        window.open(response.data.redirect_url, "_blank");
      } else {
        toast.error(
          response.data.message || "Failed to generate purchase link",
        );
      }
    } catch (error) {
      console.error("Purchase error:", error);
      toast.error("Failed to initiate purchase");
    } finally {
      setIsPurchasing(false);
    }
  };


  const handleAttachmentClick = async (e, fileUrl, video) => {
    let hasAccess = true;

    if (video.membership_ids?.length && role !== "administrator") {
      // ✅ IAP users get full access (paid from mobile)
      if (userMeta?.has_active_iap === true) {
        hasAccess = true;
      } else {
        const normalizedVideoMembershipIds = (video.membership_ids || [])
          .map((id) => {
            const num = Number(id);
            return isNaN(num) ? 0 : num;
          })
          .filter((id) => id > 0);

        hasAccess = normalizedVideoMembershipIds.some((id) =>
          userMembershipIds.includes(id),
        );
      }
    }

    if (!hasAccess) {
      toast.error("File is restricted");
      handleMembershipClick(video.membership_ids);
      return;
    } else {
      e.stopPropagation();

      // your logic here
      window.open(fileUrl, "_blank");
    }
  };


  const handleVideoClick = async (video) => {
    const isAdmin = role === "administrator";
    const hasActiveIap = userMeta?.has_active_iap === true;

    let hasAccess;

    if (isAdmin) {
      hasAccess = true;
    } else if (role === "trader") {
      // Traders in US30: membership or IAP only, no channel assignment
      const expiry = video.membership_expiry || [];
      const ids = video.membership_ids || [];

      if (expiry.length > 0) {
        hasAccess = checkVideoAccess(
          expiry,
          userMembershipIds,
          isAdmin,
          hasActiveIap,
        );
      } else if (ids.length > 0) {
        const paidIds = ids.map(Number).filter((id) => id > 0);
        hasAccess =
          hasActiveIap || paidIds.some((id) => userMembershipIds.includes(id));
      } else {
        hasAccess = true;
      }
    } else if (video.membership_expiry && video.membership_expiry.length > 0) {
      hasAccess = checkVideoAccess(
        video.membership_expiry,
        userMembershipIds,
        isAdmin,
        hasActiveIap,
      );
    } else {
      if (!video.membership_ids?.length) {
        hasAccess = true;
      } else if (hasActiveIap) {
        hasAccess = true;
      } else {
        const paidIds = (video.membership_ids || [])
          .map(Number)
          .filter((id) => id > 0);
        hasAccess = paidIds.some((id) => userMembershipIds.includes(id));
      }
    }

    if (!hasAccess) {
      toast.error("Video is restricted");
      handleMembershipClick(video.membership_expiry || []);
      return;
    }

    if (video.url.includes("vimeo")) {
      const regex = /vimeo\.com\/(\d+)(?:\/([a-zA-Z0-9]+))?/;
      const match = video.url.match(regex);
      if (match && match[1]) {
        setSelectedVideo({ type: "vimeo", id: match[1], hash: match[2] });
        setShowVideoModal(true);
      }
    } else {
      window.open(video.url, "_blank", "noopener,noreferrer");
    }
  };
  const handleJoinSession = () => {
    if (!us30) return;
    const now = new Date();
    const sessionStart = new Date(us30.start_datetime);
    if (now >= sessionStart) {
      setIsZoomMeetingScreen();
      setSelectedZoomSession(us30);
    } else {
      setShowPopup(true);
    }
  };

  // Prefer new key, fallback to old
  const videoTabs = us30Content?.video_tabs || us30Content?.video_links || [];
  const currentTab = videoTabs[activeTabIndex];
  const groups = currentTab?.groups || [];

  if (loading) {
    return (
      <div className={styles.mainContainer}>
        <div className={styles.appBar}>
          <h2 className={styles.title}>US30</h2>
        </div>
        <div className={styles.scrollableContent}>
          <div style={{ textAlign: "center", padding: "50px" }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.mainContainer}>
      <div className={styles.appBar}>
        <h2 className={styles.title}>US30</h2>
      </div>

      <div className={styles.scrollableContent}>
        {us30Content?.banner_image && (
          <img
            src={`${ENDPOINTS.IMAGE_BASE_URL}${us30Content.banner_image}`}
            className={styles.bannerImage}
            alt="US30 Banner"
          />
        )}

        {us30Content?.title_text && (
          <h2 className={styles.subtitle}>{us30Content.title_text}</h2>
        )}

        {us30Content?.description_text && (
          <div className={styles.descriptionContainer}>
            <h4 className={styles.description}>
              {us30Content.description_text}
            </h4>
          </div>
        )}

        {(us30Content?.founder_image || us30Content?.secondary_text) && (
          <div className={styles.container}>
            {us30Content?.secondary_text && (
              <div className={styles.textSection}>
                <h4 className={styles.description}>
                  {us30Content.secondary_text}
                </h4>
              </div>
            )}
            {us30Content?.founder_image && (
              <div className={styles.imageSection}>
                <img
                  src={`${ENDPOINTS.IMAGE_BASE_URL}${us30Content.founder_image}`}
                  className={styles.introImage}
                  alt="Founder"
                />
              </div>
            )}
          </div>
        )}

        {/* VIDEO TABS */}
        {videoTabs.length > 0 && (
          <div className={styles.videoSection}>
            <h2 className={styles.subtitle}>Watch videos</h2>

            <div className={styles.tabsContainer}>
              {videoTabs.map((tab, index) => (
                <button
                  key={index}
                  className={`${styles.tabButton} ${activeTabIndex === index ? styles.activeTab : ""}`}
                  onClick={() => setActiveTabIndex(index)}
                >
                  {tab.tab_name}
                </button>
              ))}
            </div>

            <div className={styles.groupsContainer}>
              {groups.map((group, groupIndex) => (
                <div key={groupIndex} className={styles.videoGroup}>
                  <h3 className={styles.groupTitle}>{group.group_title}</h3>

                  {(isMobileApp() && group.group_banner_mobile_url) && (
                    <img
                      src={ENDPOINTS.IMAGE_BASE_URL + group.group_banner_mobile_url}
                      className={styles.bannerImage}
                      alt="Content"
                    />
                  )}

                  {(!isMobileApp() && group.group_banner_web_url) && (
                    <img
                      src={ENDPOINTS.IMAGE_BASE_URL + group.group_banner_web_url}
                      className={styles.bannerImage}
                      alt="Content"
                    />
                  )}

                  {group.group_description && (
                    <p className={styles.groupDescription}>
                      {group.group_description}
                    </p>
                  )}

                  <div className={styles.videoGrid}>
                    {(group.videos || [])
                      .filter((video) => {
                        if (role === "administrator") return true;

                        const expiry = video.membership_expiry || [];
                        if (expiry.length === 0) return true;

                        const now = new Date();

                        // If ALL plans for this video are expired → hide for everyone
                        const allPlansExpired = expiry.every(
                          (e) => e.expires_at && new Date(e.expires_at) <= now,
                        );
                        if (allPlansExpired) return false;

                        // User-specific: if user has matching plans and ALL are expired → hide
                        const userRelevantExpiries = expiry.filter((e) =>
                          userMembershipIds.includes(Number(e.membership_id)),
                        );

                        if (userRelevantExpiries.length === 0) return true;

                        return !userRelevantExpiries.every(
                          (e) => e.expires_at && new Date(e.expires_at) <= now,
                        );
                      })
                      .map((video, videoIndex) => {
                        const thumbnailKey = `${currentTab.id}-${group.id}-${video.id}`;
                        let videoImage = videoPlaceholderImage;
                        if (video.cover_image) {
                          videoImage =
                            ENDPOINTS.IMAGE_BASE_URL + video.cover_image;
                        } else if (thumbnailCache[thumbnailKey]) {
                          videoImage = thumbnailCache[thumbnailKey];
                        }

                        return (
                          <div
                            key={videoIndex}
                            className={styles.videoCard}
                            onClick={() => handleVideoClick(video)}
                          >
                            <div className={styles.videoThumbnailWrapper}>
                              <img
                                src={videoImage}
                                className={styles.videoThumbnail}
                                style={{ objectFit: "cover" }}
                                alt={video.title || "Video"}
                              />
                              <div className={styles.videoOverlay}>
                                <div className={styles.playIcon}>▶</div>
                              </div>
                            </div>
                            {video.title && (
                              <div className={styles.videoTitle}>
                                {video.title}
                              </div>
                            )}
                            {video.attach_file && (
                              <AttachmentLink
                                fileUrl={
                                  ENDPOINTS.IMAGE_BASE_URL + video.attach_file
                                }
                                                                video={video}
                                onClick={handleAttachmentClick}

                              />
                            )}
                          </div>

                        );
                      })}

                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Live Session */}
        {us30 && (
          <div style={{ width: "100%" }}>
            <h2 className={styles.subtitle}>Join Live Session</h2>
            <button className={styles.submitBtn} onClick={handleJoinSession}>
              ▶
            </button>
            {timeLeft ? (
              <div className={styles.timerContainer}>
                {["days", "hours", "minutes", "seconds"].map((unit) => (
                  <div key={unit} className={styles.timerBlock}>
                    <div className={styles.timerValue}>{timeLeft[unit]}</div>
                    <div className={styles.timerLabel}>
                      {unit.charAt(0).toUpperCase() + unit.slice(1)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.sessionUnavailable}>Session started.</div>
            )}
          </div>
        )}
      </div>

      {/* Popup Modal */}
      {showPopup && (
        <div
          className={styles.popupOverlay}
          onClick={() => setShowPopup(false)}
        >
          <div
            className={styles.popupContent}
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Session Not Started Yet</h3>
            <p>Please wait for the session to start</p>
            <button
              className={styles.popupButton}
              onClick={() => setShowPopup(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Vimeo Modal */}
      {showVideoModal && selectedVideo && (
        <div
          className={styles.videoModalOverlay}
          onClick={() => {
            setShowVideoModal(false);
            setSelectedVideo(null);
          }}
        >
          <div
            className={styles.videoModalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={styles.closeButton}
              onClick={() => {
                setShowVideoModal(false);
                setSelectedVideo(null);
              }}
            >
              ✕
            </button>
            {selectedVideo.type === "vimeo" && (
              <iframe
                src={
                  selectedVideo.hash
                    ? `https://player.vimeo.com/video/${selectedVideo.id}?h=${selectedVideo.hash}&autoplay=1&title=0&byline=0&portrait=0&controls=1`
                    : `https://player.vimeo.com/video/${selectedVideo.id}?autoplay=1&title=0&byline=0&portrait=0&controls=1`
                }
                className={styles.videoIframe}
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title="Vimeo Video"
              />
            )}
          </div>
        </div>
      )}

      {/* Plan Selection Modal */}
      {showPlanSelection && (
        <div className={styles.popupOverlay}>
          <div className={styles.popupContent}>
            <h3>Choose a Membership Plan</h3>
            {availablePlans.map((plan) => (
              <div key={plan.id} style={{ marginBottom: 16 }}>
                <h4>{plan.name || plan.label}</h4>
                <p>
                  {plan.payment_type === "free" ? "Free" : `$${plan.price}`}
                </p>
                <button
                  className={styles.popupButton}
                  disabled={isPurchasing}
                  onClick={() => {
                    handlePurchaseMembership(plan.id);
                    setShowPlanSelection(false);
                  }}
                >
                  {isPurchasing ? (
                    <>
                      <FaSpinner className="fa-spin" /> Processing...
                    </>
                  ) : (
                    "Select Plan"
                  )}
                </button>
              </div>
            ))}
            <button
              className={styles.popupButton}
              onClick={() => setShowPlanSelection(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Us30Page;
